import React, { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import analyticsService from "../../services/analyticsService";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const pad2 = (n) => String(n).padStart(2, "0");

const toDateInputValue = (d) => {
  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${year}-${month}-${day}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const formatMoney = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatCompact = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
};

const Analytics = () => {
  const today = useMemo(() => new Date(), []);

  const [from, setFrom] = useState(toDateInputValue(startOfMonth(today)));
  const [to, setTo] = useState(toDateInputValue(today));
  const [granularity, setGranularity] = useState("daily");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (str.includes("\"")) return `"${str.replace(/"/g, '""')}"`;
    if (str.includes(",") || str.includes("\n")) return `"${str}"`;
    return str;
  };

  const downloadCsv = (rows, filename) => {
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await analyticsService.getSummary({ from, to, granularity });
      setData(resp.data.data);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors.join(", ") : null) ||
        e?.message ||
        "Failed to load analytics";
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = data?.totals || {};
  const series = Array.isArray(data?.series) ? data.series : [];

  const completionRate = (() => {
    const total = Number(totals.total_orders || 0);
    const completed = Number(totals.completed_orders || 0);
    if (!total) return 0;
    return (completed / total) * 100;
  })();

  const handleExport = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Analytics Summary"]);
    rows.push(["Metric", "Value"]);
    rows.push(["Total Income", totals.total_income ?? ""]);
    rows.push(["Total Visits", totals.total_visits ?? ""]);
    rows.push(["Total Orders", totals.total_orders ?? ""]);
    rows.push(["Total Patients", totals.total_patients ?? ""]);
    rows.push(["Completed Orders", totals.completed_orders ?? ""]);
    rows.push(["Active Orders", totals.active_orders ?? ""]);
    rows.push(["Completion Rate %", completionRate.toFixed(2)]);
    rows.push(["Avg Income Per Visit", totals.avg_income_per_visit ?? ""]);
    rows.push([]);

    rows.push(["Breakdown"]);
    rows.push(["Period", "Income", "Orders", "Visits"]);
    series.forEach((row) => {
      rows.push([
        row.period,
        row.income,
        row.orders,
        row.visits,
      ]);
    });

    downloadCsv(rows, "analytics-report.csv");
  };

  const Card = ({ label, value, sub }) => (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-100">
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-[#235F72]">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-[#235F72]">Analytics</h2>
            <div className="mt-1 text-sm text-gray-500">
              Daily / Monthly metrics for a selected time range.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Group by</label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent"
              >
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={handleExport}
                disabled={!data || loading}
                className={
                  "h-10 px-3 rounded-lg font-semibold border border-gray-200 text-[#235F72] bg-white hover:bg-gray-50 transition duration-200 flex items-center justify-center gap-2 " +
                  (!data || loading ? "opacity-60 cursor-not-allowed" : "")
                }
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className={
                  "h-10 w-full rounded-lg font-semibold transition duration-200 " +
                  (loading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#235F72] text-white hover:bg-[#1a4a5a]")
                }
              >
                {loading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            {String(error)}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total Income" value={`${formatMoney(totals.total_income)} ETB`} sub={`${from} → ${to}`} />
        <Card label="Total Visits" value={String(totals.total_visits ?? 0)} sub="Distinct visits" />
        <Card label="Total Orders" value={String(totals.total_orders ?? 0)} sub="Test orders" />
        <Card label="Total Patients" value={String(totals.total_patients ?? 0)} sub="Distinct patients" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card label="Completed Orders" value={String(totals.completed_orders ?? 0)} sub="Status = completed" />
        <Card label="Active Orders" value={String(totals.active_orders ?? 0)} sub="Ordered / collected / in progress" />
        <Card
          label="Completion Rate"
          value={`${formatMoney(completionRate)}%`}
          sub="Completed ÷ total orders"
        />
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-[#235F72]">Trends</div>
            <div className="mt-1 text-sm text-gray-500">
              Income vs Orders/Visits over time ({granularity}).
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Income shown as ETB (right axis)
          </div>
        </div>

        <div className="mt-4 h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => formatCompact(v)}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "Income (ETB)") return [`${formatMoney(value)} ETB`, name];
                  return [String(value), name];
                }}
              />
              <Legend />

              <Bar yAxisId="left" dataKey="orders" name="Orders" fill="#36F1A2" radius={[6, 6, 0, 0]} />
              <Bar yAxisId="left" dataKey="visits" name="Visits" fill="#235F72" fillOpacity={0.25} radius={[6, 6, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="income"
                name="Income (ETB)"
                stroke="#235F72"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-[#235F72]">Breakdown</div>
            <div className="mt-1 text-sm text-gray-500">
              {granularity === "monthly" ? "Monthly" : "Daily"} totals in the selected range.
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Avg income/visit: <span className="font-semibold text-gray-900">{formatMoney(totals.avg_income_per_visit)} ETB</span>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-2 pr-4">Period</th>
                <th className="py-2 pr-4">Income (ETB)</th>
                <th className="py-2 pr-4">Orders</th>
                <th className="py-2 pr-4">Visits</th>
              </tr>
            </thead>
            <tbody>
              {series.length ? (
                series.map((row) => (
                  <tr key={row.period} className="border-b last:border-b-0">
                    <td className="py-2 pr-4 font-medium text-gray-900">{row.period}</td>
                    <td className="py-2 pr-4 text-gray-900">{formatMoney(row.income)}</td>
                    <td className="py-2 pr-4 text-gray-900">{row.orders}</td>
                    <td className="py-2 pr-4 text-gray-900">{row.visits}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-500">
                    {loading ? "Loading..." : "No data for the selected range."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
